import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import PATHS, { mergePaths } from 'utils/paths';

import { faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

import DCWorkingHourForm from '../DCWorkingHours/DCWorkingHourForm';
import AddLockerDimensionsForm from '../LockerDimensions/LockerDimensionsForm';
import OTODeliveryPriceForm from '../OTODeliveryPrices/OTODeliveryPriceForm';
import OTOMarginForm from '../OTOMargin/OTOMarginForm';
import ShippingContractForm from '../ShippingContracts/ShippingContractForm';
import SpecialContractTermForm from '../SpecialContractTerms/SpecialContractTermForm';
import SuccessInstructionsForm from '../SuccessInstructions/SuccessInstructionsForm';

/**
 * @author @muhammedburakak
 */

const ContractConfigurations = () => {
	const { pathname } = useLocation();

	const tabs = useMemo(
		() => [
			{
				label: 'shippingContracts',
				path: mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SHIPPING_CONTRACTS)
			},
			{
				label: 'otoDeliveryPrices',
				path: mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_DELIVERY_PRICES)
			},
			{
				label: 'successInstructions',
				path: mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SUCCESS_INSTRUCTIONS)
			},
			{
				label: 'specialContractTerms',
				path: mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SPECIAL_CONTRACT_TERMS)
			},
			{
				label: 'otoMargin',
				path: mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_MARGIN)
			}
		],
		[]
	); //

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/reasonConfig-icon.json.gz"
				title="contractConfigurations">
				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SHIPPING_CONTRACTS) && (
					<Button
						icon={faPlus}
						label="addDCWorkingHour"
						onClick={() => NiceModal.show(DCWorkingHourForm)}
						variant="blackOutline"
					/>
				)}
				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SHIPPING_CONTRACTS) && (
					<Button
						icon={faPlus}
						label="addLockerDimensions"
						onClick={() => NiceModal.show(AddLockerDimensionsForm)}
						variant="blackOutline"
					/>
				)}

				{/* Import button for each tab */}
				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SHIPPING_CONTRACTS) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.shippingContractImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_DELIVERY_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.otoDeliveryPriceImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SUCCESS_INSTRUCTIONS) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.successInstructionsImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SPECIAL_CONTRACT_TERMS) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.specialContractTermImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_MARGIN) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.otoMarginImport)}
					/>
				)}

				{/* Add button for each tab */}
				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SHIPPING_CONTRACTS) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addShippingContract"
						onClick={() => NiceModal.show(ShippingContractForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_DELIVERY_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addOTODeliveryPrice"
						onClick={() => NiceModal.show(OTODeliveryPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SUCCESS_INSTRUCTIONS) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addSuccessInstruction"
						onClick={() => NiceModal.show(SuccessInstructionsForm, { mode: 'add' })}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.SPECIAL_CONTRACT_TERMS) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addSpecialContractTerm"
						onClick={() => NiceModal.show(SpecialContractTermForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.CONTRACT_CONFIGURATIONS, PATHS.OTO_MARGIN) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addOTOMargin"
						onClick={() => NiceModal.show(OTOMarginForm)}
						variant="blue"
						className="addButton"
					/>
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default ContractConfigurations;
